/**
 * LoginPanel — Authentication UI for the Alterspective Instruct add-in.
 *
 * Displays an instance URL input and "Sign In" button. Shows spinner
 * during authentication and error messages on failure.
 */

import React, { useState } from 'react';
import {
  Input,
  Label,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PlugConnected20Regular } from '@fluentui/react-icons/svg/plug-connected';
import { useAuth } from '../../auth/auth-context';
import { canUseDevLogin, getDemoInstanceUrl } from '../../auth/auth-service';

const BRAND_COLOR = '#075156';
const DEMO_INSTANCE_URL = getDemoInstanceUrl();

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '24px',
    gap: '24px',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 700,
    color: BRAND_COLOR,
    letterSpacing: '0',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    marginTop: '-16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '320px',
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  button: {
    marginTop: '4px',
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
  },
  spinnerText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  errorBar: {
    width: '100%',
    maxWidth: '320px',
  },
  hint: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
  },
  buttonRow: {
    display: 'flex',
    gap: '8px',
    width: '100%',
  },
  secondaryButton: {
    flex: 1,
  },
  devButton: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
});

export const LoginPanel: React.FC = () => {
  const styles = useStyles();
  const { instanceUrl, setInstanceUrl, login, isAuthenticating, error, clearError, gatewayUrl } = useAuth();
  const showDevLogin = canUseDevLogin(gatewayUrl);
  const showDemoLogin = showDevLogin && !!DEMO_INSTANCE_URL;
  const [localUrl, setLocalUrl] = useState(instanceUrl || (showDemoLogin ? DEMO_INSTANCE_URL : ''));

  const normaliseUrl = (raw: string): string => {
    const url = raw.trim();
    if (!url) {
      return '';
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    if (/^(localhost|127\.0\.0\.1|\[::1\])(?::\d+)?(?:\/.*)?$/i.test(url)) {
      return `http://${url}`;
    }
    return `https://${url}`;
  };

  const handleSignIn = () => {
    clearError();
    if (!localUrl.trim()) return;
    const normalised = normaliseUrl(localUrl);
    setInstanceUrl(normalised);
    login(normalised);
  };

  const handleDevLogin = () => {
    clearError();
    if (!localUrl.trim()) return;
    const normalised = normaliseUrl(localUrl);
    setInstanceUrl(normalised);
    login(normalised, true);
  };

  const handleDemoLogin = () => {
    if (!DEMO_INSTANCE_URL) return;
    clearError();
    setLocalUrl(DEMO_INSTANCE_URL);
    setInstanceUrl(DEMO_INSTANCE_URL);
    login(DEMO_INSTANCE_URL, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSignIn();
    }
  };

  if (isAuthenticating) {
    return (
      <div className={styles.container}>
        <div className={styles.logo}>ALTERSPECTIVE</div>
        <div className={styles.spinnerContainer}>
          <Spinner size="medium" label="Signing in..." />
          <span className={styles.spinnerText}>
            Connecting to your practice management system...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.logo}>ALTERSPECTIVE</div>
      <div className={styles.subtitle}>Instruct — Advice Workflow</div>

      {error && (
        <MessageBar className={styles.errorBar} intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.form}>
        <div className={styles.inputWrapper}>
          <Label htmlFor="instance-url" size="small">
            Instance URL
          </Label>
          <Input
            id="instance-url"
            placeholder="firm.sharedo.tech"
            value={localUrl}
            onChange={(_e, data) => setLocalUrl(data.value)}
            onKeyDown={handleKeyDown}
            disabled={isAuthenticating}
            appearance="outline"
          />
        </div>

        <div className={styles.buttonRow}>
          <Button
            className={styles.button}
            appearance="primary"
            icon={<PlugConnected20Regular />}
            onClick={handleSignIn}
            disabled={!localUrl.trim() || isAuthenticating}
            style={{ backgroundColor: BRAND_COLOR, flex: 1 }}
          >
            Sign In
          </Button>
          {showDemoLogin && (
            <Button
              className={styles.secondaryButton}
              appearance="secondary"
              onClick={handleDemoLogin}
              disabled={isAuthenticating}
            >
              Local Demo
            </Button>
          )}
        </div>
      </div>

      <div className={styles.hint}>
        {showDemoLogin
          ? `Demo instance ready at ${DEMO_INSTANCE_URL}`
          : 'Enter your practice management system URL to connect.'}
      </div>

      {showDevLogin && (
        <Button
          className={styles.devButton}
          appearance="transparent"
          size="small"
          onClick={handleDevLogin}
          disabled={!localUrl.trim() || isAuthenticating}
        >
          Dev Login (Service Account)
        </Button>
      )}
    </div>
  );
};
